import React, { useState, useEffect, useRef } from "react";
import { worldAPI } from "../../../../../../engine/modules/world/worldAPI";
import WorldValueInput from "./WorldValueInput";
import GridSettingsSection from "./GridSettingsSection";
import FOVInput from "../../../FOVInput.js";

function WorldEditor() {
  const [transform, setTransform] = useState({ x: 0, y: 0, z: 0 });
  const [gridSkyScale, setGridSkyScale] = useState(1.0);
  const [gridTextureRepeat, setGridTextureRepeat] = useState(1.0);
  const [globalscale, setGlobalscale] = useState(1.0);
  const [sceneFOV, setSceneFOV] = useState(65);
  const [gridVisible, setGridVisible] = useState(null);
  const [gridColorMode, setGridColorMode] = useState("light");

  const getWorldSettings = () => {
    const settings = worldAPI.getAllSettings();
    setTransform(settings.transform);
    setGridSkyScale(settings.grid.scale);
    setGridTextureRepeat(settings.grid.tiling);
    setGlobalscale(settings.globalScale);
    setSceneFOV(settings.sceneFOV);
    setGridVisible(settings.grid.visible);
    setGridColorMode(settings.grid.colorMode);
  };

  useEffect(() => {
    getWorldSettings();

    // Subscribe to worldAPI changes
    const unsubscribe = worldAPI.subscribe((change) => {
      if (change.type === "grid") {
        getWorldSettings();
      }
    });

    return unsubscribe;
  }, []);

  const handleTransformChange = (key, value) => {
    const newTransform = { ...transform, [key]: value };
    worldAPI.setGlobalTransform(newTransform);
    getWorldSettings();
  };

  const handleHelp = (e) => {
    e.stopPropagation();
    window.open("https://docs.inventum3d.com/world/", "_blank");
  };

  const handleSceneFOVChange = (value) => {
    worldAPI.setSceneFOV(value);
    setSceneFOV(value);
  };

  return (
    <div className="LabelsPanel">
      <div className="EditorTabHeader">
        <div className="EditorTabTitle">
          <i className="material-icons notranslate">public</i>
          <span>World</span>
        </div>
        <button className="EditorTabHelpButton" title="Opens the Inventum Docs" onClick={handleHelp}>
          <i className="material-icons notranslate">help</i>
        </button>
      </div>
      <WorldDrawer
        setSceneFOV={handleSceneFOVChange}
        sceneFOV={sceneFOV}
        setTransform={handleTransformChange}
        getSettings={getWorldSettings}
        transform={transform}
        gridSkyScale={gridSkyScale}
        gridTextureRepeat={gridTextureRepeat}
        globalscale={globalscale}
        gridVisible={gridVisible}
        gridColorMode={gridColorMode}
        setGridVisible={(state) => {
          worldAPI.setGridVisible(state);
          getWorldSettings();
        }}
      />
    </div>
  );
}

const SCENE_CONTENT_SCALE_TOOLTIP =
  "Scales all scene content, including models, labels, lights, slides, and saved camera views. It does not resize the visual grid or sky.";
const GRID_SKY_SCALE_TOOLTIP =
  "Scales the visual grid and sky/background only. It does not change model, label, light, slide, or camera-view positions.";

function WorldDrawer(props) {
  return (
    <div className="LabelDrawCollection">
      <div className="WorldEditorContent">
      <div className="WorldSection">
        <h3 className="WorldSectionTitle">Transform</h3>
        
        <div className="WorldGroup">
          <label className="WorldLabel">Global Position</label>
          <div className="WorldTransformInputs">
            <WorldValueInput
              name="X"
              inputWidth="67.5px"
              paddingWidth="5px"
              value={props.transform.x}
              valueKey="x"
              handleUpdate={props.setTransform}
            />
            <WorldValueInput
              name="Y"
              inputWidth="67.5px"
              paddingWidth="5px"
              value={props.transform.z}
              valueKey="z"
              handleUpdate={props.setTransform}
            />
            <WorldValueInput
              name="Z"
              inputWidth="67.5px"
              paddingWidth="5px"
              value={props.transform.y}
              valueKey="y"
              handleUpdate={props.setTransform}
            />
          </div>
        </div>

        <div className="WorldGroup">
          <WorldValueInput
            step={0.01}
            min={0.01}
            name="Scene Content Scale"
            inputWidth="67.5px"
            value={props.globalscale}
            marginTop="0px"
            tooltip={SCENE_CONTENT_SCALE_TOOLTIP}
            handleUpdate={(value) => {
              worldAPI.setGlobalScale(value);
              props.getSettings();
            }}
          />
        </div>

        <div className="WorldGroup">
          <WorldValueInput
            step={0.01}
            min={0.01}
            name="Grid & Sky Scale"
            inputWidth="67.5px"
            value={props.gridSkyScale}
            tooltip={GRID_SKY_SCALE_TOOLTIP}
            handleUpdate={(value) => {
              worldAPI.setGridScale(value);
              props.getSettings();
            }}
          />
        </div>

      </div>

      <GridSettingsSection
        gridVisible={props.gridVisible}
        gridTextureRepeat={props.gridTextureRepeat}
        gridColorMode={props.gridColorMode}
        getSettings={props.getSettings}
        setGridVisible={props.setGridVisible}
      />

      <div className="WorldSection">
        <h3 className="WorldSectionTitle">Camera & Scene</h3>

        <div className="WorldGroup">
          <label className="WorldLabel">Start View</label>
          <div className="WorldButtonGroup">
            <button
              className="WorldButton"
              onClick={() => {
                if (window.Inventum && window.Inventum.camera) {
                  window.Inventum.camera.showStartView();
                }
              }}>
              Show
            </button>
            <button
              className="WorldButton"
              onClick={() => {
                if (window.Inventum && window.Inventum.camera) {
                  window.Inventum.camera.setInitialPosition();
                }
              }}>
              Set
            </button>
          </div>
        </div>

        <div className="WorldGroup">
          <label className="WorldLabel">Start Visibility</label>
          <div className="WorldButtonGroup">
            <button
              className="WorldButton"
              onClick={() => {
                if (window.Inventum && window.Inventum.world) {
                  window.Inventum.world.showVisibleStart();
                }
              }}>
              Show
            </button>
            <button
              className="WorldButton"
              onClick={() => {
                if (window.Inventum && window.Inventum.world) {
                  window.Inventum.world.setVisibleStart();
                }
              }}>
              Set
            </button>
          </div>
          <div className="WorldHint">Sets initial model/label visibility</div>
        </div>

        <div className="WorldGroup">
          <label className="WorldLabel">Scene FOV</label>
          <div className="WorldSliderControl">
            <input
              type="range"
              min="20"
              max="100"
              className="WorldSlider"
              onChange={(e) => {
                e.stopPropagation();
                props.setSceneFOV(parseInt(e.target.value));
              }}
              value={props.sceneFOV}
            />
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <FOVInput
                value={props.sceneFOV}
                min={20}
                max={100}
                onChange={(value) => props.setSceneFOV(value)}
                className="WorldValueInput"
              />
              <span className="WorldValueUnit">°</span>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

export default WorldEditor;
